<script lang="ts" setup>
import { useI18n } from 'vue-i18n';
import { useDisplay } from 'vuetify';
import useFoldersService from '@/composables/apiServices/useFoldersService';
import type {
  IFilterActionsModel,
  IFilterActionsProps
} from '@/types/composables/filter-shared/useFilterActions';
import { computed } from 'vue';
import useDisabledFeature from '@/composables/useDisabledFeature';
import { EDisabledFeatures } from '@/types/composables/useDisabledFeatureEnums';
import TargetsInput from '@/components/settings/TargetsInput.vue';

const { t } = useI18n();
const display = useDisplay();

const { getFlatFolders } = useFoldersService();
const { data: folderList } = getFlatFolders();

const { isFeatureDisabled } = useDisabledFeature();
const isTargetsFeatureDisabled = await isFeatureDisabled(EDisabledFeatures.TARGETS);
const model = defineModel<IFilterActionsModel>({ required: true });
const props = defineProps<IFilterActionsProps>();

const computedFolderList = computed(() => {
  return (
    folderList.value?.map((folder) => ({
      title: folder.fullName,
      value: folder.id
    })) ?? []
  );
});

const computedMailboxModel = computed({
  get: () => model.value.mailbox.value ?? [],
  set: (value: string) => {
    model.value.toggles.canMoveTo.value = !!value;
    model.value.mailbox.value = value;
  }
});
const computedTargetsModel = computed({
  get: () => model.value.targets.value ?? [],
  set: (value: string[]) => {
    model.value.toggles.canTargets.value = !!value.length;
    model.value.targets.value = value;
  }
});
</script>

<template>
  <p class="my-4 font-weight-medium" sentry-unmask>
    {{ t('settings.filters.form.actions.description') }}
  </p>
  <!-- todo: need to get folder list for folder select-->
  <v-row
    class="align-start justify-space-between"
    :class="{ 'ga-4': display.smAndUp.value }"
    no-gutters
  >
    <v-col :cols="display.smAndUp.value ? 4 : 12">
      <v-checkbox
        v-model="model.toggles.canMoveTo.value"
        :disabled="props.isPending"
        :label="t('settings.filters.form.actions.moveToFolder')"
        color="primary"
        hide-details
        sentry-unmask
      />
    </v-col>
    <v-col :cols="display.smAndUp.value ? 7 : 12">
      <VSelect
        id="filter-moveToFolder-select"
        v-model="computedMailboxModel"
        :disabled="props.isPending"
        :error-messages="props.errors['action.mailbox']"
        :items="computedFolderList"
        :placeholder="t('settings.filters.form.actions.moveToFolder')"
        class="move-to"
        clearable
        data-test-id="settings-filter-moveToFolder-select"
        variant="outlined"
        :list-props="{ density: 'compact', maxWidth: '508' }"
      />
    </v-col>
  </v-row>

  <div class="pb-2">
    <template v-if="!isTargetsFeatureDisabled">
      <v-row
        class="align-start justify-space-between"
        :class="{ 'ga-4': display.smAndUp.value }"
        no-gutters
      >
        <v-col :cols="display.smAndUp.value ? 4 : 12">
          <v-checkbox
            v-model="model.toggles.canTargets.value"
            :disabled="props.isPending"
            :label="t('settings.filters.form.actions.forwardTo')"
            color="primary"
            hide-details
            sentry-unmask
          />
        </v-col>
        <v-col :cols="display.smAndUp.value ? 7 : 12">
          <TargetsInput
            v-model="computedTargetsModel"
            :error-messages="props.errors['action.targets']"
            :label="t('settings.filters.form.actions.forwardTo')"
            target-label=""
            single-line
          />
        </v-col>
      </v-row>
    </template>
    <v-row class="align-start py-2" no-gutters>
      <v-col :cols="display.smAndUp.value ? '' : 12">
        <v-checkbox-btn
          v-model="model.seen.value"
          :disabled="props.isPending"
          :error-messages="props.errors['action.seen']"
          :label="t('settings.filters.form.actions.checkboxes.markAsRead')"
          color="primary"
          sentry-unmask
        />
      </v-col>
      <v-col :cols="display.smAndUp.value ? '' : 12">
        <v-checkbox-btn
          v-model="model.delete.value"
          :disabled="props.isPending"
          :error-messages="props.errors['action.delete']"
          :label="t('settings.filters.form.actions.checkboxes.delete')"
          color="primary"
          sentry-unmask
        />
      </v-col>
      <v-col :cols="display.smAndUp.value ? '' : 12">
        <v-checkbox-btn
          v-model="model.flag.value"
          :disabled="props.isPending"
          :error-messages="props.errors['action.flag']"
          :label="t('settings.filters.form.actions.checkboxes.markAsFlagged')"
          color="primary"
          sentry-unmask
        />
      </v-col>
    </v-row>
  </div>

  <VLabel class="pb-2 cursor-pointer text-title-md on-surface" sentry-unmask>
    {{ t('settings.filters.form.actions.junkMailActions') }}
  </VLabel>
  <VRadioGroup
    v-model="model.spam.value"
    :disabled="props.isPending"
    :error-messages="props.errors['action.spam']"
    sentry-unmask
  >
    <v-row no-gutters>
      <v-col :cols="display.smAndUp.value ? '' : 12">
        <VRadio :label="t('settings.filters.form.actions.radios.doNothing')" value="nothing" />
      </v-col>
      <v-col :cols="display.smAndUp.value ? '' : 12">
        <VRadio :label="t('settings.filters.form.actions.radios.markAsJunk')" value="spam" />
      </v-col>
      <v-col :cols="display.smAndUp.value ? '' : 12">
        <VRadio
          :label="t('settings.filters.form.actions.radios.doNotMarkAsJunk')"
          value="notspam"
        />
      </v-col>
    </v-row>
  </VRadioGroup>
</template>

<style lang="scss" scoped>
.targets-input {
  &:deep(.v-combobox__selection) {
    gap: 1rem;
  }
}
</style>
